import { Clear, Extension, Visibility } from "@material-ui/icons";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import moment from "moment";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { deletePreview, uploadPreview } from "../../api/RequestForm";
import { NewIDataUploadItem } from "../../interfaces/IDataUploadItem";
import {
  FilterSpecialCharacterFileName,
  TrimFilename,
} from "../../shared/functions";
import { selectUserProfile } from "../../store/auth/selector";
import {
  ACCEPTED_EXTENSIONS,
  EMAIL_EXTENSIONS,
  getFileExtension,
  getOfficePreviewUrl,
  getSharePointUrl,
  getTempPath,
  isValidFileSize,
} from "../../utils/fileUtils";
import ConfirmationDialog from "../dialog/simple-alert";
import Feedback from "../feedback/feedback";
import Loading from "../loading/loading";

interface Prop {
  file: NewIDataUploadItem;
  fileIndex: number;
  onAddMoreData(): void;
  onDeleteData(index: number): void;
  onUploadFile(file: any, fileIndex: number): void;
  onDeleteFile(fileIndex: number): void;
  onDownloadFile(fileIndex: number): void;
  requiredValidationMessage: string;
  disabled?: boolean;
}

const resetFileInput = (event: HTMLInputElement) => {
  // eslint-disable-next-line no-param-reassign
  event.type = "text";
  // eslint-disable-next-line no-param-reassign
  event.type = "file";
};

export default function UploadMultipleFileComponent({
  file,
  fileIndex,
  onAddMoreData,
  onDeleteData,
  onDeleteFile,
  onUploadFile,
  onDownloadFile,
  requiredValidationMessage,
  disabled = false,
}: Prop) {
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackType, setFeedbackType] = useState("");
  const [openFeedback, setOpenFeedback] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dataIndex, setDataIndex] = useState(0);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [loadingActive, setLoadingActive] = useState(false);
  const profile = useSelector(selectUserProfile);

  const fileUrl = useMemo(() => {
    const currentFile = file.files[dataIndex];
    const ext = getFileExtension(currentFile.fileName);

    const isAcceptedExt = ACCEPTED_EXTENSIONS.has(ext as any);
    const isEmailFormat = EMAIL_EXTENSIONS.includes(ext as any);

    if (isAcceptedExt) {
      if (isEmailFormat && currentFile.fileBase64) {
        return getSharePointUrl(getTempPath(profile.BinusianId, ext));
      }

      if (currentFile.fileBase64) {
        return currentFile.fileBase64;
      }

      return getSharePointUrl(currentFile.folderName);
    }

    if (!currentFile.documentID) {
      const tempPath = getTempPath(profile.BinusianId, ext);
      return getOfficePreviewUrl(getSharePointUrl(tempPath));
    }

    return getOfficePreviewUrl(getSharePointUrl(currentFile.folderName));
  }, [dataIndex, file, profile.BinusianId]);

  const showFeedback = (message: string, type: "failed" | "success") => {
    setFeedbackText(message);
    setFeedbackType(type);
    setOpenFeedback(true);
    setTimeout(() => {
      setOpenFeedback(false);
    }, 3000);
  };

  const handleUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    fileInd: number
  ) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    const uploadDate = moment().format("DDMMYYYY").toString();
    const fileName = uploadedFile.name.split(".");
    fileName.pop();
    const extensionName = getFileExtension(uploadedFile.name);

    const trimmedFileName = TrimFilename(
      `${fileName}_${uploadDate}.${extensionName}`
    );

    const files = new File([uploadedFile], trimmedFileName, {
      type: uploadedFile.type,
    });

    const filenameValidation = FilterSpecialCharacterFileName(files.name);
    if (!filenameValidation.isValid) {
      showFeedback(filenameValidation.message, "failed");
      resetFileInput(event.target);
      return;
    }

    if (!isValidFileSize(files.size)) {
      showFeedback("Size file must less than 10MB", "failed");
      resetFileInput(event.target);
      return;
    }

    onUploadFile(files, fileInd);
    resetFileInput(event.target);
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="subtitle1" gutterBottom>
          {file.title.split("/").map((part, partIndex, partsArray) => (
            <span key={part}>
              {partIndex === 1 ? (
                <span style={{ fontStyle: "italic" }}>
                  <span>
                    {part.includes("*") ? (
                      <>
                        <u>{part.replace("*", "")}</u>
                        <span style={{ color: "red" }}> *</span>
                      </>
                    ) : (
                      <u>{part}</u>
                    )}
                  </span>
                </span>
              ) : (
                <u>{part}</u>
              )}
              {partIndex < partsArray.length - 1 && (
                <u>
                  <span>/</span>
                </u>
              )}
            </span>
          ))}
          {file.fileType == "Surat Persetujuan Penggunaan Master Dokumen" ? (
            <span style={{ color: "red", fontSize: "12px" }}> </span>
          ) : (
            !disabled && (
              <span style={{ color: "red", fontSize: "12px" }}>
                {" "}
                (max.10MB)
              </span>
            )
          )}
        </Typography>
      </div>
      {file.files.map((el, ind) => (
        <div
          style={{
            display: "flex",
            backgroundColor: disabled ? "lightgray" : "white",
            marginBottom:
              disabled && ind + 1 == file.files.length ? 0 : "0.3rem",
            padding: "0.5rem",
            borderRadius: "0.375rem",
            border: `1px solid ${disabled ? "#a3a3a3" : "#c4c4c4"}`,
          }}
        >
          <div
            className="flexbox"
            style={{ width: "95%", alignItems: "center" }}
          >
            {!disabled && (
              <div className="chooseFile">
                <label
                  htmlFor={`${file.title}-${fileIndex}-${ind}`}
                  className="labelChooseFile"
                >
                  Choose File
                  <input
                    type="file"
                    name="file"
                    style={{ color: "transparent" }}
                    id={`${file.title}-${fileIndex}-${ind}`}
                    onChange={(e) => handleUpload(e, ind)}
                    accept="application/pdf,
                            application/zip,
                            application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                            application/vnd.openxmlformats-officedocument.presentationml.presentation,
                            image/png,
                            image/jpeg,
                            application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
                            application/vnd.ms-excel,
                            application/vnd.ms-powerpoint,
                            application/msword,
                            .msg"
                  />
                </label>
              </div>
            )}
            <div style={{ display: "flex", width: "18%" }}>
              <Tooltip title="Download" placement="top">
                <IconButton
                  onClick={() => onDownloadFile(ind)}
                  style={{ padding: 0 }}
                >
                  <FileDownloadOutlinedIcon style={{ color: "#6C2882" }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="View" placement="top">
                <IconButton
                  onClick={async () => {
                    const ext = getFileExtension(file.files[ind].fileName);
                    if (
                      file.files[ind].documentID == "" &&
                      ext != "pdf" &&
                      ext != "jpg" &&
                      ext != "jpeg" &&
                      ext != "png" &&
                      ext != "rar"
                    ) {
                      setLoadingActive(true);
                      await uploadPreview({
                        fileBase64: file.files[ind].fileBase64,
                        fileName: `${profile.BinusianId}.${ext}`,
                      })
                        .then(() => {
                          setDataIndex(ind);
                          setOpenPreviewDialog(true);
                          setLoadingActive(false);
                        })
                        .catch((error) => {
                          setLoadingActive(false);
                          setFeedbackText(
                            "Preview failed, please check your connection."
                          );
                          setFeedbackType("failed");
                          setOpenFeedback(true);
                          setTimeout(() => {
                            setOpenFeedback(false);
                          }, 3000);
                        });
                    } else {
                      setDataIndex(ind);
                      setOpenPreviewDialog(true);
                    }
                  }}
                  style={{ padding: 0 }}
                >
                  <Visibility style={{ color: "#6C2882" }} />
                </IconButton>
              </Tooltip>
              {!disabled && (
                <Tooltip title="Delete" placement="top">
                  <IconButton
                    onClick={() => onDeleteFile(ind)}
                    style={{ padding: 0 }}
                  >
                    <DeleteForeverOutlinedIcon style={{ color: "#6C2882" }} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <div style={{ wordBreak: "break-word" }}>
              <Typography>{el.fileName}</Typography>
            </div>
          </div>
          {file.files.length > 1 && !disabled && (
            <div>
              <Button
                color="error"
                onClick={() => {
                  setDataIndex(ind);
                  setOpenDialog(true);
                }}
                style={{ padding: "0.2rem", minWidth: "min-content" }}
              >
                <Clear />
              </Button>
            </div>
          )}
        </div>
      ))}
      {!disabled && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "0.5rem",
          }}
        >
          <div className="error-message">{requiredValidationMessage}</div>
          <Button
            variant="contained"
            onClick={onAddMoreData}
            style={{
              paddingTop: "0.2rem",
              paddingBottom: "0.2rem",
              paddingLeft: "0.75rem",
              paddingRight: "0.75rem",
              height: "auto",
              textTransform: "lowercase",
            }}
          >
            add files
          </Button>
        </div>
      )}
      <Feedback
        open={openFeedback}
        message={feedbackText}
        feedbackType={feedbackType}
        onClose={() => {
          if (feedbackType == "failed") {
            setOpenFeedback(false);
          }
        }}
      />
      <ConfirmationDialog
        open={openDialog}
        title="Confirmation"
        message="Are you sure you want to delete ?"
        onConfirm={() => {
          setDataIndex(0);
          onDeleteData(dataIndex);
          setOpenDialog(false);
        }}
        onCancel={() => {
          setOpenDialog(false);
        }}
      />
      <Dialog open={openPreviewDialog} maxWidth={false}>
        <DialogTitle
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <Typography>{file.files[dataIndex].fileName}</Typography>
          <Box
            display="flex"
            sx={{ cursor: "pointer" }}
            onClick={async () => {
              const ext = getFileExtension(file.files[dataIndex].fileName);
              if (
                file.files[dataIndex].documentID == "" &&
                ext != "pdf" &&
                ext != "jpg" &&
                ext != "jpeg" &&
                ext != "png" &&
                ext != "rar"
              ) {
                deletePreview(
                  `${profile.BinusianId}.${getFileExtension(
                    file.files[dataIndex].fileName
                  )}`
                );
                setOpenPreviewDialog(false);
              } else {
                setOpenPreviewDialog(false);
              }
            }}
          >
            <Clear />
          </Box>
        </DialogTitle>
        <DialogContent
          style={{ padding: "1rem", paddingBottom: 0, paddingTop: 0 }}
        >
          {getFileExtension(file.files[dataIndex].fileName) !== "zip" ? (
            <iframe
              height={window.innerHeight * 0.7}
              width={window.innerWidth * 0.6}
              src={fileUrl}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #ccc",
                borderRadius: "4px",
                boxShadow:
                  "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
                height: "70vh",
                width: "60vw",
                padding: "16px",
                boxSizing: "border-box",
              }}
            >
              <Extension color="disabled" style={{ fontSize: 48 }} />
              <Typography color="disabled" variant="h4" align="center">
                File extension is not supported for preview.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          style={{
            padding: "0.5rem",
            paddingRight: "1rem",
            paddingBottom: "1rem",
          }}
        >
          <Button onClick={() => onDownloadFile(dataIndex)} variant="contained">
            Download File
          </Button>
        </DialogActions>
      </Dialog>
      <Loading open={loadingActive} />
    </>
  );
}
